import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { backofficeClient } from "@/lib/api/backoffice-client";

/** backoffice-api's own endpoints: the proxy would carry the operator's identity into the tenant. */
const ADMINS_PATH = "/tenant-admins";
const BOOTSTRAP_PATH = "/tenant-admins/bootstrap";

/** Same cap as backoffice-api BootstrapTenantAdminsRequest. */
export const MAX_BOOTSTRAP_ADMINS = 50;

/** Mirrors backoffice-api TenantAdmin. */
export interface TenantAdmin {
  employee_id: string;
  email: string;
  name: string;
}

/** Mirrors backoffice-api TenantAdminsGroup. */
export interface TenantAdminsGroup {
  group_id: string;
  group_name: string;
  /** False when an admin removed the preset in the product; a bootstrap restores it. */
  has_platform_admin: boolean;
  admins: TenantAdmin[];
}

/** Mirrors backoffice-api TenantAdminsResponse. `group` is null for a tenant never bootstrapped. */
interface TenantAdminsResponse {
  group: TenantAdminsGroup | null;
}

/** Mirrors backoffice-api BootstrapTenantAdminsResponse. */
export interface BootstrapResult {
  group_id: string;
  group_name: string;
  group_created: boolean;
  admins: { employee_id: string; work_email: string }[];
}

interface BootstrapInput {
  tenant_id: string;
  emails: string[];
}

export const tenantAdminsQueryKey = ["tenant-admins"] as const;

export function useTenantAdmins(tenantId: string) {
  return useQuery({
    queryKey: [...tenantAdminsQueryKey, tenantId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<TenantAdminsResponse>(
        ADMINS_PATH,
        { params: { tenant_id: tenantId }, routeTenantId: tenantId },
      );
      return data.group;
    },
    enabled: Boolean(tenantId),
  });
}

export function useBootstrapTenantAdmins() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: BootstrapInput) => {
      const { data } = await backofficeClient.post<BootstrapResult>(
        BOOTSTRAP_PATH,
        input,
        { routeTenantId: input.tenant_id },
      );
      return data;
    },
    // Also after a failure: the employees may exist and the group may be
    // half-updated, and the read is what shows the operator the real state
    onSettled: (_result, _error, input) => {
      void queryClient.invalidateQueries({
        queryKey: [...tenantAdminsQueryKey, input.tenant_id],
      });
    },
  });
}

/**
 * Turns raw operator input into the list the endpoint takes: one address per
 * line, comma or whitespace, lowercased and deduplicated the way backoffice-api
 * normalizes them, so the confirmation shows exactly what will be sent.
 */
export function parseEmails(raw: string): string[] {
  const seen = new Set<string>();
  for (const token of raw.split(/[\s,;]+/)) {
    const email = token.trim().toLowerCase();
    if (email !== "") seen.add(email);
  }
  return [...seen];
}

// Deliberately loose: backoffice-api validates for real; this only catches
// a pasted name or a missing "@" before the operator confirms.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function invalidEmails(emails: string[]): string[] {
  return emails.filter((email) => !EMAIL_SHAPE.test(email));
}

/**
 * backoffice-api answers with a plain `detail`, or for emails that could not
 * become employees an object with `message` and a per-email `failures` map;
 * FastAPI validation errors carry a list of them.
 */
export function tenantAdminsErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (!isAxiosError<{ detail?: unknown }>(error)) return fallback;
  const detail: unknown = error.response?.data.detail;
  if (typeof detail === "string") return detail;
  if (typeof detail === "object" && detail !== null && "failures" in detail) {
    const { message, failures } = detail as {
      message?: unknown;
      failures?: unknown;
    };
    const lines =
      typeof failures === "object" && failures !== null
        ? Object.entries(failures as Record<string, unknown>).map(
            ([email, reason]) => `${email}: ${String(reason)}`,
          )
        : [];
    const head = typeof message === "string" ? message : fallback;
    return lines.length > 0 ? `${head} — ${lines.join("; ")}` : head;
  }
  if (Array.isArray(detail)) {
    const messages = detail.flatMap((item: unknown) =>
      typeof item === "object" &&
      item !== null &&
      "msg" in item &&
      typeof item.msg === "string"
        ? [item.msg]
        : [],
    );
    if (messages.length > 0) return messages.join("; ");
  }
  return fallback;
}
